import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import { useConsent } from "@hooks/useConsent";
import AuthButton from "@memorang/ui/components/AuthButton";
import Box from "@memorang/ui/components/Box";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { useGlobalSearchParams } from "expo-router";
import { useMemo } from "react";
import { Checkbox, useTheme } from "react-native-paper";

type Props = {
	onContinue: () => void;
};

const ConsentContainer = ({ onContinue }: Props) => {
	const theme = useTheme();
	const { auth_token, redeemCode } = useGlobalSearchParams<{
		auth_token: string;
		redeemCode: string;
	}>();
	const appConfig = useAppStore((store) => store.app);
	const consentConfig = appConfig.landing?.consent;
	const links = appConfig.links;

	const {
		requiresExplicitConsent,
		isConsentChecked,
		handleConsentChange,
		handleContinue,
		isSaving,
	} = useConsent();

	const isDeeplinkFlow = !!auth_token || !!redeemCode;

	const appName = useAppStore((store) => store.app.name);

	const privacyNoticeUrl = links?.privacy || "";
	const mobileAppTermsUrl = links?.tos || "";
	const aiToolTermsUrl = links?.aiToolsTos || "";

	const selectedConsentText = consentConfig?.text?.[i18n.language];

	const consentMarkdown = useMemo(() => {
		if (selectedConsentText) {
			if (aiToolTermsUrl) {
				return `${selectedConsentText} and [ai tool terms of use](${aiToolTermsUrl})`;
			}
			return selectedConsentText;
		}

		const privacyNoticeLink = `[${appName}'s candidate privacy notice](${privacyNoticeUrl})`;
		const mobileAppTermsLink = `[mobile app terms](${mobileAppTermsUrl})`;
		const aiToolTermsLink = `[ai tool terms of use](${aiToolTermsUrl})`;

		if (aiToolTermsUrl) {
			return `By continuing, I agree to ${privacyNoticeLink}, ${mobileAppTermsLink}, and ${aiToolTermsLink}`;
		}

		return `By continuing, I agree to ${privacyNoticeLink} and ${mobileAppTermsLink}`;
	}, [
		selectedConsentText,
		privacyNoticeUrl,
		mobileAppTermsUrl,
		aiToolTermsUrl,
		appName,
	]);

	const handleContinuePress = () => {
		handleContinue(onContinue);
	};

	const isContinueDisabled = requiresExplicitConsent && !isConsentChecked;

	if (isDeeplinkFlow) {
		return null;
	}

	return (
		<Box width="100%" gap={12} alignItems="center">
			<Box flexDirection="row" alignItems="center" gap={8} width="100%">
				{requiresExplicitConsent && (
					<Checkbox.Android
						status={isConsentChecked ? "checked" : "unchecked"}
						onPress={() => handleConsentChange(!isConsentChecked)}
						accessibilityLabel={consentMarkdown}
					/>
				)}
				<Box flex={1}>
					<CustomMarkdownRenderer
						text={consentMarkdown}
						fontSize={12}
						textAlign={requiresExplicitConsent ? "left" : "center"}
						customStyles={{
							text: {
								color: theme.colors.onSurfaceVariant,
							},
						}}
					/>
				</Box>
			</Box>
			<AuthButton
				testID="continue-with-email"
				icon="email-outline"
				fullWidth
				onPress={handleContinuePress}
				disabled={isContinueDisabled || isSaving}
			>
				Continue with email
			</AuthButton>
		</Box>
	);
};

export default ConsentContainer;
